import { Typography, Space, Button } from 'antd'
import {
  PreviewOptionTypename,
  useDesignLayers,
} from '../../../../hooks/useDesignLayers'
import { useCallback, useEffect, useState } from 'react'
import { SelectOption, Selector } from 'components/Selector'
import {
  useAsphaltPlacementMapImageLazyQuery,
  useConcretePlacementMapImageLazyQuery,
  useLotMapImageLazyQuery,
  useDailyInspectionControlPointMapImageLazyQuery,
  useDailyInspectionInspectionMapImageLazyQuery,
  useDailyInspectionPunchListMapImageLazyQuery,
} from 'schema'
import LoadableImage from 'components/LoadableImage'
import { LayerPreviewProps } from './types'
import { DefaultQueryHookOptions } from 'utils/defaults'
import { errorHandler } from 'hooks/errorHandler'

const LayerPreview: React.FC<LayerPreviewProps> = ({
  option,
  currentLayers: projectLayerIds,
}) => {
  const { selectOptionMessage, previewOptions } = useDesignLayers(option)
  const [globalId, setGlobalId] = useState<string>()
  const [typename, setTypename] = useState<PreviewOptionTypename>()
  const [src, _setSrc] = useState<string | undefined | null>()
  const [loading, setLoading] = useState(false)

  const setSrc = useCallback(
    (response?: { base64MapImage?: string | null }) => {
      _setSrc(response?.base64MapImage)
      setLoading(false)
    },
    []
  )
  const [asphalt] = useAsphaltPlacementMapImageLazyQuery({
    ...DefaultQueryHookOptions,
    onCompleted: ({ asphaltPlacement }) => setSrc(asphaltPlacement),
  })
  const [concretePlacement] = useConcretePlacementMapImageLazyQuery({
    ...DefaultQueryHookOptions,
    onCompleted: ({ concretePlacement }) => setSrc(concretePlacement),
  })
  const [lot] = useLotMapImageLazyQuery({
    ...DefaultQueryHookOptions,
    onCompleted: ({ lot }) => setSrc(lot),
  })
  const [dailyInspectionControlPoint] =
    useDailyInspectionControlPointMapImageLazyQuery({
      ...DefaultQueryHookOptions,
      onCompleted: ({ dailyInspectionControlPoint }) =>
        setSrc(dailyInspectionControlPoint),
    })
  const [dailyInspectionInspection] =
    useDailyInspectionInspectionMapImageLazyQuery({
      ...DefaultQueryHookOptions,
      onCompleted: ({ dailyInspectionInspection }) =>
        setSrc(dailyInspectionInspection),
    })
  const [dailyInspectionPunchList] =
    useDailyInspectionPunchListMapImageLazyQuery({
      ...DefaultQueryHookOptions,
      onCompleted: ({ dailyInspectionPunchList }) =>
        setSrc(dailyInspectionPunchList),
    })

  useEffect(() => {
    setGlobalId(undefined)
    setTypename(undefined)
    _setSrc(undefined)
  }, [option])

  const [options, setOptions] = useState<SelectOption[]>([])
  const [recordOptions, setRecordOptions] = useState<SelectOption[]>([])
  useEffect(() => {
    setOptions(
      previewOptions?.map(({ option: { __typename: value, name: label } }) => ({
        label,
        value,
      })) ?? []
    )
    const option = previewOptions?.find(
      ({ option: { __typename } }) => typename === __typename
    )
    setRecordOptions(option?.options ?? [])
  }, [typename, previewOptions])

  return (
    <Space direction="vertical" size="middle" style={{ maxWidth: 600 }}>
      <Typography.Text>
        {!!projectLayerIds
          ? 'Select a record type to generate a preview with the configured project feature layer(s)'
          : selectOptionMessage({ preview: true })}
      </Typography.Text>
      {!!projectLayerIds && (
        <>
          <Space>
            <Selector
              options={options}
              placeholder="Select a record type"
              currentValue={typename}
              onChange={(value) => {
                setTypename(
                  options.find((option) => option.value === value)
                    ?.value as PreviewOptionTypename
                )
                setGlobalId(undefined)
              }}
              disabled={loading}
            />
            <Selector
              options={recordOptions}
              currentValue={globalId}
              onChange={(globalId) => setGlobalId(globalId as string)}
              disabled={!typename || loading}
              placeholder={typename ? 'Select a record' : undefined}
            />
            <Button
              type="primary"
              loading={loading}
              disabled={!typename || !globalId || loading}
              onClick={() => {
                if (!globalId) return errorHandler('globalId is not defined')
                setLoading(true)
                const options = {
                  variables: { globalId, projectLayerIds },
                }
                if (typename === 'AsphaltPlacement') asphalt(options)
                else if (typename === 'ConcretePlacement')
                  concretePlacement(options)
                else if (typename === 'DailyInspectionControlPoint')
                  dailyInspectionControlPoint(options)
                else if (typename === 'DailyInspectionInspection')
                  dailyInspectionInspection(options)
                else if (typename === 'DailyInspectionPunchList')
                  dailyInspectionPunchList(options)
                else if (typename === 'Lot') lot(options)
              }}
            >
              Generate Preview
            </Button>
          </Space>
          <LoadableImage
            height={400}
            width={600}
            alt={`${option?.name} preview`}
            loading={loading}
            src={src}
            preview
          />
        </>
      )}
    </Space>
  )
}

export default LayerPreview
